import { useEffect, useState } from 'react';
import { EntryRequestDto } from '@api/models/invitation/EntryRequestDto';
import { useTranslation } from 'react-i18next';
import { CellDate } from '../common/grid/CellDate';
import { QcsTableCell } from '../common/basic/QcsTableCell';
import { DateFormat } from '../../utils/date';
import { parseISO } from 'date-fns';
import { InnerTooltipWrapper, QcsTooltip } from '../common/basic/QcsTooltip';
import { TableRowButton } from '../common/basic/TableRowButton';
import { RequestEntryInfoModal } from './RequestEntryInfoModal';
import { FaIcon } from '../common/FaIcon';
import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router';
import { Grid } from '../common/grid/Grid';
import { useAppDispatch, useAppSelector } from '../../store';
import { entryRequestListActions, getEntryRequestList, selectEntryRequestList } from '../../store/entities/entryRequestList';
import { SectionHeader } from '../common/SectionHeader';
import { CancelToken } from 'axios';
import { EntryRequestFilter } from './EntryRequestFilter';
import { isExternal } from '../../utils/roles';
import { selectIdentity } from '../../store/entities/identity';

const MAX_INVITATION_CHARS = 25;

export const EntryRequests = () => {
  const entryRequestList = useAppSelector(selectEntryRequestList);
  const { t } = useTranslation();
  const [entryRequestDetail, setEntryRequestDetail] = useState<EntryRequestDto>();
  const navigate = useNavigate();
  const identity = useAppSelector(selectIdentity);
  const dispatch = useAppDispatch();

  const handleGetData = (cancelToken: CancelToken) => {
    dispatch(getEntryRequestList(cancelToken));
  };

  const handleAddModalClose = () => {
    setEntryRequestDetail(undefined);
  };

  const handleRowClick = (item: EntryRequestDto) => {
    setEntryRequestDetail(item);
  };

  const handleOpenEntryRequestDetail = (item: EntryRequestDto) => {
    navigate(`/invitation/${item.invitation.id}/entryRequest?entryRequestId=${item.id}&backTo=/entryRequest`);
  };

  const renderData = (item: EntryRequestDto) => {
    return (
      <>
        <QcsTableCell>{item.supplierEmployees?.[0]?.supplier?.name ?? ''}</QcsTableCell>
        <QcsTableCell>
          {item.invitation.name && item.invitation.name.length > MAX_INVITATION_CHARS ? (
            <QcsTooltip title={item.invitation.name} placement="top">
              <span>{item.invitation.name.substring(0, MAX_INVITATION_CHARS)}...</span>
            </QcsTooltip>
          ) : (
            item.invitation.name
          )}
        </QcsTableCell>
        {item.dateTo === item.dateFrom ? (
          <CellDate value={item.dateFrom} />
        ) : (
          <QcsTableCell>
            <DateFormat value={parseISO(item.dateFrom!)} showWithTime={false} /> {' - '}
            <DateFormat value={parseISO(item.dateTo!)} showWithTime={false} />
          </QcsTableCell>
        )}
        <QcsTableCell>{item.establishment.name}</QcsTableCell>
        <QcsTableCell>{item.workplaces?.map((it) => it.name)?.join(', ')}</QcsTableCell>
        <QcsTableCell>{t(`invitation.entryRequest.state.${item.state}`)}</QcsTableCell>
        <QcsTableCell align="right">
          <QcsTooltip title={t('invitation.tabs.tooltips.openEntryRequestDetail')} placement="top">
            <InnerTooltipWrapper>
              <TableRowButton
                onClick={() => handleOpenEntryRequestDetail(item)}
              >
                <FaIcon icon={faUpRightFromSquare} />
              </TableRowButton>
            </InnerTooltipWrapper>
          </QcsTooltip>
        </QcsTableCell>
      </>
    );
  };

  useEffect(() => {
    const external = isExternal(identity);
    if (!external) {
      dispatch(
        entryRequestListActions.updateFilter({
          responsiblePersonCompany: [{ id: identity.id, name: identity.name }],
        })
      );
    }
  }, [dispatch, identity]);

  return (
    <>
      {entryRequestDetail && <RequestEntryInfoModal data={entryRequestDetail} onClose={handleAddModalClose} />}
      <SectionHeader title="invitation.entryRequest.title" />
      <Grid<EntryRequestDto>
        headers={[
          { captionStr: 'invitation.entryRequest.table.supplier' },
          { captionStr: 'invitation.entryRequest.table.invitation' },
          { captionStr: 'invitation.entryRequest.table.date', orderName: 'dateFrom' },
          { captionStr: 'invitation.entryRequest.table.establishment' },
          { captionStr: 'invitation.entryRequest.table.workplaces' },
          { captionStr: 'invitation.entryRequest.table.status', orderName: 'state' },
          {},
        ]}
        renderData={renderData}
        gridActions={entryRequestListActions}
        onRowClick={handleRowClick}
        getData={handleGetData}
        search={false}
        data={entryRequestList}
        filter={<EntryRequestFilter query={entryRequestList.query} />}
      />
    </>
  );
};
